import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { UserPlus, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

const CAMPO = 'w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 '
  + 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 '
  + 'placeholder-slate-400 dark:placeholder-slate-500 '
  + 'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all';

const BOTAO = 'w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 '
  + 'hover:to-purple-700 text-white py-3 rounded-xl font-medium shadow-lg hover:shadow-xl '
  + 'transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2';

const MIN_SENHA = 8;

/**
 * Adesão por convite. O administrador escolhe quem entra; a pessoa define a
 * própria senha — ninguém precisa digitá-la por ela nem sabê-la.
 */
export default function Aderir() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [convite, setConvite] = useState(null);
  const [carregando, setCarregando] = useState(!!token);
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [repetir, setRepetir] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/convites/${encodeURIComponent(token)}`)
      .then(({ data }) => { setConvite(data); setNome(data.name || ''); })
      .catch((err) => setErro(err?.response?.data?.detail || 'Convite inválido.'))
      .finally(() => setCarregando(false));
  }, [token]);

  const curta = senha.length > 0 && senha.length < MIN_SENHA;
  const diferentes = repetir.length > 0 && senha !== repetir;
  const podeEnviar = senha.length >= MIN_SENHA && senha === repetir && !enviando;

  const enviar = async (e) => {
    e.preventDefault();
    if (!podeEnviar) return;
    setErro(''); setEnviando(true);
    try {
      await api.post('/auth/aderir', { token, password: senha, name: nome });
      setPronto(true);
    } catch (err) {
      setErro(err?.response?.data?.detail || 'Não foi possível criar a conta.');
    } finally { setEnviando(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 border border-indigo-100 dark:border-slate-700">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-2xl shadow-lg">
              {pronto ? <CheckCircle2 className="w-12 h-12 text-white" /> : <UserPlus className="w-12 h-12 text-white" />}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">D'Pots</h1>

          {carregando && <p className="text-center text-slate-500 dark:text-slate-400 mt-4">Verificando o convite...</p>}

          {!carregando && (!token || (!convite && !pronto)) && (
            <>
              <p className="text-gray-600 dark:text-slate-400 text-center mb-6" data-testid="aderir-invalido">
                {erro || 'Este endereço não traz um convite válido.'}
              </p>
              <Link to="/login" className={BOTAO}>Ir para o login</Link>
            </>
          )}

          {!carregando && convite && !pronto && (
            <>
              <p className="text-gray-600 dark:text-slate-400 text-center mb-2">Crie sua conta</p>
              <p className="text-sm text-center text-slate-500 dark:text-slate-400 mb-8" data-testid="aderir-email">
                <span className="font-medium text-slate-700 dark:text-slate-200">{convite.email}</span>
              </p>

              <form onSubmit={enviar} className="space-y-4">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Seu nome</label>
                  <input id="nome" data-testid="aderir-nome" value={nome} onChange={(e) => setNome(e.target.value)}
                    placeholder="Como você quer ser chamado" className={CAMPO} />
                </div>
                <div>
                  <label htmlFor="senha" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Senha</label>
                  <input id="senha" data-testid="aderir-senha" type="password" autoComplete="new-password"
                    required value={senha} onChange={(e) => setSenha(e.target.value)}
                    placeholder="••••••••" className={CAMPO} />
                  <p className={`text-xs mt-1.5 ${curta ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'}`}>
                    Pelo menos {MIN_SENHA} caracteres.
                  </p>
                </div>
                <div>
                  <label htmlFor="repetir" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Repita a senha</label>
                  <input id="repetir" data-testid="aderir-repetir" type="password" autoComplete="new-password"
                    required value={repetir} onChange={(e) => setRepetir(e.target.value)}
                    placeholder="••••••••" className={CAMPO} />
                  {diferentes && <p className="text-xs mt-1.5 text-rose-600 dark:text-rose-400">As senhas não conferem.</p>}
                </div>

                {erro && (
                  <div role="alert" className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{erro}</span>
                  </div>
                )}

                <button type="submit" data-testid="aderir-enviar" disabled={!podeEnviar} className={BOTAO}>
                  {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
                  {enviando ? 'Criando...' : 'Criar minha conta'}
                </button>
              </form>
            </>
          )}

          {pronto && (
            <>
              <p className="text-gray-600 dark:text-slate-400 text-center mb-6" data-testid="aderir-pronto">
                Conta criada. Você já pode entrar.
              </p>
              <button onClick={() => navigate('/login')} className={BOTAO}>Ir para o login</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
